import type { SpreadsheetColumnId } from '~/_shared/types/spreadsheetData/spreadsheetColumn';
import { areDeepEqual } from '~/_shared/utils/equality/deepEqual.helper';
import { mergeDeep } from '~/_shared/utils/object/deepMerge';
import { isEmpty } from '~/_shared/utils/object/isEmpty';
import { pick } from '~/_shared/utils/object/pick';
import { removeUndefined } from '~/_shared/utils/object/pickBy';
import {
  createDataWithColumnPerSpreadsheetSettings, getDataFromColumnPerSpreadsheetSettings,
} from '~/_shared/utils/spreadsheet/generalSpreadsheet.helpers';
import { isItemContactItem } from '~/customizeLocationPanel/customizeLocationPanel.constants';
import {
  getColumnItemById, modifyPanelSettingsColumnItemMap,
} from '~/customizeLocationPanel/locationData/locationData.helpers';
import type { BubbleItemMap } from '~/customizeLocationPanel/locationData/locationData.types';
import type {
  ColumnStyleSettingsData, ColumnStyleSettingsValueStyle, PanelBulkStyles, PanelLabelStyle, PanelSettingsColumnItemMap,
} from '~/customizeLocationPanel/locationData/panelSettings.types';

const mergeOrDrop = <T extends PanelLabelStyle | ColumnStyleSettingsValueStyle | ColumnStyleSettingsData>(
  defaultStyle: T,
  existing: T | undefined,
  override: Partial<T>,
): T | undefined => {
  const merged = mergeDeep<T>(
    {},
    defaultStyle,
    ...(existing ? [existing] : []),
    ...(isEmpty(override) ? [] : [override]),
  );

  return areDeepEqual(defaultStyle, merged) ? undefined : merged;
};

type BatchParams = Readonly<{
  bubbleItems: BubbleItemMap;
  bulkStyle?: PanelBulkStyles;
  defaultBulkStyle: ColumnStyleSettingsData;
  itemsData: PanelSettingsColumnItemMap;
  selectedId: SpreadsheetColumnId;
  style: Partial<ColumnStyleSettingsData>;
}>;

export const applySingleItem = (
  { itemsData, selectedId, style, defaultBulkStyle, bubbleItems, bulkStyle }: BatchParams
): { itemsData: PanelSettingsColumnItemMap; bulkStyle?: PanelBulkStyles } => {
  const current = getDataFromColumnPerSpreadsheetSettings(itemsData, selectedId);
  if (!current) {
    return { itemsData, bulkStyle };
  }

  const currentBulkStyle = bulkStyle ?? defaultBulkStyle;
  const currentItemType = getColumnItemById(bubbleItems, selectedId)?.itemType;
  if (isItemContactItem(currentItemType)) {
    const { valueFontColor, valueFontSize, labelFontSize, labelFontColor,
      showLabel, verticalGap, capitalize } = style;

    const valueOverride = removeUndefined({ valueFontColor, valueFontSize, capitalize });
    const mergedValue = mergeOrDrop(currentBulkStyle, current.style, valueOverride);
    const updatedValue = { ...current, style: mergedValue };

    const defaultLabelStyle = pick(currentBulkStyle, ['labelFontSize', 'labelFontColor', 'showLabel', 'verticalGap']);
    const labelOverride = removeUndefined({ labelFontSize, labelFontColor, showLabel, verticalGap });
    const mergedLabel = mergeOrDrop<PanelLabelStyle>(defaultLabelStyle, bulkStyle?.contactLabelStyle, labelOverride);
    const updatedBulkStyle = { ...currentBulkStyle, contactLabelStyle: mergedLabel };

    return {
      bulkStyle: updatedBulkStyle,
      itemsData: mergeDeep<PanelSettingsColumnItemMap>({},
        itemsData,
        createDataWithColumnPerSpreadsheetSettings(updatedValue, selectedId),
      ),
    };
  }
  else {
    const merged = mergeOrDrop(currentBulkStyle, current.style, style);
    const updated = { ...current, style: merged };

    return {
      itemsData: mergeDeep<PanelSettingsColumnItemMap>({},
        itemsData,
        createDataWithColumnPerSpreadsheetSettings(updated, selectedId)
      ),
      bulkStyle,
    };
  }
};

export const applyBulk = ({ itemsData, style, defaultBulkStyle }: {
  defaultBulkStyle: ColumnStyleSettingsData;
  itemsData: PanelSettingsColumnItemMap;
  style: Partial<ColumnStyleSettingsData>;
}): PanelSettingsColumnItemMap => {
  return modifyPanelSettingsColumnItemMap({
    columnSettingItems: itemsData,
    itemDataModifier: record => {
      const merged = mergeOrDrop<ColumnStyleSettingsData>(
        defaultBulkStyle,
        record.style,
        style
      );
      const drop = areDeepEqual(defaultBulkStyle, merged);

      return { ...record, style: drop ? undefined : merged };
    },
  });
};
